import type {
  MedicationRouteCode,
  MedicationSchedule,
} from '../medication/schema';

export interface MedpraxResolveSPOParams {
  schemeCode: string;
  planDescription?: string;
}

export interface MedpraxSPO {
  schemeCode: string;
  planCode: string;
  optionCode: string;
  subOptionCode: string;
}

export interface MedpraxQualificationSearchRequest {
  query?: string;
}

export interface MedpraxSchemesAdministratorResponse {
  SchemeAdministrator: MedpraxSchemeAdministrator;
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxSchemesAdministratorAllResponse {
  SchemeAdministrators: MedpraxSchemeAdministrator[];
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxIcd10AllResponse {
  Icd10s: Icd10S;
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxSchemeAdministrator {
  Code: string;
  Name: string;
  DiscontinuedDate: Date | null;
  SchemeCodeList?: string[];
}

export interface MedpraxSchemesSchemeResponse {
  Scheme: MedpraxScheme;
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxScheme {
  DateFormat?: string;
  Name: string;
  Code: string;
  SchemeAdministrator?: MedpraxSchemeAdministrator;
  SchemeAdministratorCode: string;
  ManagedCareCompany?: MedpraxManagedCareCompany;
  ManagedCareCompanyCode?: string;
  CaptitionScheme?: string;
  DiscontinuedDate?: string;
  CmsReferenceNumber?: string;
  Category?: string;
  SchemeTelephoneNumber?: string;
  SchemeEmail?: string;
  SchemeUrl?: string;
  PlanCodeList?: string[];
}

export interface MedpraxOption {
  Code: string;
  Option: string;
  PlanCode: string;
}

export interface MedpraxSubOption {
  Code: string;
  Option: string;
  PlanOptionCode: string;
}

export interface MedpraxPlan {
  Code: string;
  Option: string;
  SchemeCode: string;
}

export interface MedpraxManagedCareCompany {
  Code: string;
  Name: string;
  TollFree: string;
  TelephoneNumber: string;
  FaxNumber: string;
  Email: string;
  Url: string;
}

export interface MedpraxSchemesSchemeAllResponse {
  Schemes: MedpraxScheme[];
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxSchemesPlansAllResponse {
  Plans: MedpraxPlan[];
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxSchemesPlanOptionAllResponse {
  PlanOptions: MedpraxOption[];
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxSchemesPlanSubOptionAllResponse {
  PlanSubOptions: MedpraxSubOption[];
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxCodeModel {
  code: string;
  model: boolean;
}

export interface MedpraxIcd10Search {
  searchQuery?: string;
  isChronic?: boolean;
}

export interface MedpraxGetICD10ByCodesRequest {
  codes: string[];
}

export type MedpraxGetMedicationsByCodesResponses = MedpraxMedicationResponse[];
export type MedpraxGetMedicationsByCodesResponse = MedpraxMedicationResponse;

export interface MedpraxGetMedicationsByCodesRequest {
  codes: string[];
}

export type MedpraxGetICD10ByCodesResponses = MedpraxPageResult[];

export type MedpraxGetICD10ByCodesResponse = MedpraxPageResult;

export interface MedpraxPage {
  _page: number;
}

export interface MedpraxQueryParameters {
  year?: number;
  filterjoin: string;
  asc: boolean;
  orderPropertyName: string;
  pageIndex: number;
  pageSize: number;
}

export interface MedpraxDisciplineListResponse {
  TariffDisciplines: MedpraxPageResponse;
  DataError: null;
  IsDemoData: boolean;
}

export interface MedpraxMedicationSearchParams {
  query: string;
  presentationCodes?: MedicationRouteCode[];
  scheduleCodes?: MedicationSchedule[];
}

export interface MedpraxConsumableSearchParams {
  query: string;
}

export type MedpraxMedicationSearchResponses = MedpraxMedicationResponse[];
export type MedpraxMedicationSearchResponse = MedpraxMedicationResponse;

export interface MedpraxMedicationTokenResponse {
  DataError: null;
  Token: string;
  FetchAfterDateUtc: string;
}

export interface MedpraxMedicationQueryParameters {
  token: string;
}

export interface MedpraxMedicationResponse {
  Code: string;
  Name: string;
  NappiCodeFirstSixDigits: string;
  NappiCodeSuffixLastThreeDigits: number;
  // StrengthMetric1 maps to `Medication strength`
  StrengthMetric1?: string;
  UnitOfMeasure1?: string;
  // VolumeMetric2 maps to `Medication volume`
  VolumeMetric2?: string;
  UnitOfMeasure2?: string;
  // VolumeMetric3 maps to `Medication individualPackSize`
  VolumeMetric3?: string;
  UnitOfMeasure3?: string;
  PresentationCode: string;
  DispensingVolumePacksize: string;
  StartDate: Date;
  EndDate: Date;
  ManufacturerCode: null;
  ManufacturerDescription: string;
  Barcode: string;
  Schedule: string;
  FullDescription: string;
  objectID?: string;
}

export interface MedpraxPageResponse {
  HasPreviousPage: boolean;
  HasNextPage: boolean;
  PageIndex: number;
  PageSize: number;
  TotalPages: number;
  TotalCount: number;
  ThisCount: number;
  PageResult: MedpraxPageResult[];
}

interface MedpraxManufacturer {
  Code: string;
  Description: string;
}

export interface MedpraxPageResult {
  Description: string;
  Code: string;
  TariffMedicalType: null;
  TariffMedicalTypeCode: MedpraxTariffMedicalTypeCode;
  ChapterDescription: string;
  GroupCode: string;
  GroupDescription: string;
  Code3: string;
  Code3Description: string;
  ClinicalUse: boolean;
  PrimaryUse: boolean;
  Asterisk: boolean;
  Dagger: boolean;
  Sequelae: boolean;
  AgeRange: string;
  Gender: string;
  IsPmb: boolean;
  IsCdl: boolean;
  NappiLinkCodeList: null;
  objectID?: string;
  DateFormat: string;
  Name: string;
  NappiCodeFirstSixDigits: string;
  NappiCodeSuffixLastThreeDigits: string;
  NappiCode: string;
  StrengthMetric1: string;
  UnitOfMeasure1: string;
  VolumeMetric2: string;
  UnitOfMeasure2: string;
  VolumeMetric3: string;
  UnitOfMeasure3: string;
  Presentation: MedpraxManufacturer;
  PresentationCode: string;
  StandardPacksize: number;
  DispensingVolumePacksize: string;
  VolumePackFlag: boolean;
  DrugSchedule: string;
  AtcLevel5Code: string;
  AtcLevel5: null;
  Manufacturer: MedpraxManufacturer;
  ManufacturerCode: string;
  NonSubstitutableProductFlag: boolean;
  MedpraxGenericIdCode: string;
  GenericIndicatorCode: string;
  ProductStatus: boolean;
  SingleExitPrice: number;
  SingleExitPriceStartDate: Date;
  SingleExitPriceEndDate: string;
  EdlFlag: boolean;
  OncologyFlag: boolean;
  CatalogueNumber: string;
  Barcode: string;
  Product0201: number;
  ProductIndicator: null;
  ProductSize: string;
  RxUnit: string;
  ScriptingDosage: string;
  GenderFlag: string;
  MaximumAdultDosage: string;
  DrugExclusionDescriptionCodeList: null;
  AdministrationRouteCodeList: null;
  ActiveSubstanceDescriptionCodeList: null;
  ActiveSubstanceList: null;
  DrugClassCodeList: null;
  DrugClassList: null;
  MimsCodeList: null;
  FullDescription: string;
  ReferencePriceFileList: null;
  FormularyList: null;
  Icd10List: null;
}

export enum MedpraxTariffMedicalTypeCode {
  Mp = 'MP',
}

export interface Temperatures {
  Icd10s: Icd10S;
  DataError: null;
  IsDemoData: boolean;
}

export interface Icd10S {
  HasPreviousPage: boolean;
  HasNextPage: boolean;
  PageIndex: number;
  PageSize: number;
  TotalPages: number;
  TotalCount: number;
  ThisCount: number;
  PageResult: MedpraxPageResult[];
}
